import * as moment from "moment";
import { trace } from "owa-trace";
import RestClient from "sh-rest-client";
import PollHelper from "sh-application/utility/PollHelper";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { updateAssignOpenShiftsJobStatus, clearAssignOpenShiftsJob, setShowSuccessToast, setPartialSuccessToast, setShowFailureToast } from "sh-application/components/autoSchedule/mutatorActions/assignOpenShiftsPollingActions";
import { default as InstrumentationService } from "./InstrumentationService/InstrumentationService";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import type { UndoData } from "sh-application/components/autoSchedule/mutatorActions/assignOpenShiftsPollingActions";
import { assignOpenShiftsPollingState } from "sh-application/components/autoSchedule/store/assignOpenShiftsPollingState";
import { trackChanges } from "sh-change-store";
import ECSConfigService from "./ecsconfigservice/ECSConfigService";
import ECSConfigKey from "./ecsconfigservice/ECSConfigKey";
import DataServices from "../dataservice/AllDataServicesInstance";
import schedulesViewStateStore from "sh-application/components/schedules/lib/store/store";
import { IOpenShiftEntity } from "sh-models";
import ShiftUtils from "sh-application/utility/ShiftUtils";

const TERMINAL_STATUSES: JobStatus[] = [JobStatus.Success, JobStatus.Failure, JobStatus.Canceled];
const POLL_INTERVAL_MS = 5_000;

/**
 * Polls GET /api/jobs/teams/{teamId}/autogeneration/tags/status every 5 seconds for a specific tag.
 * Stops automatically when the job reaches a terminal status (Success/Failure/Canceled).
 */
class AssignOpenShiftsPollingService {
    private _pollHelper: PollHelper | undefined;
    private _isPolling: boolean = false;

    public startPolling(teamId: string, tagId: string): void {
        this.stopPolling();
        this._isPolling = true;

        const performPoll = async (): Promise<void> => {
            if (!this._isPolling) { return; }
            try {
                const response = await RestClient.getAutoGenerationTagsStatus(teamId);
                // Guard against cancel being clicked while the request was in-flight:
                // stopPolling() sets _isPolling = false synchronously, so if Cancel was
                // clicked before this response arrived we bail out here and skip GDIDR.
                if (!this._isPolling) { return; }
                const jobs: any[] = response?.data?.jobs || [];
                const job = jobs.find((j: any) => j.tagId === tagId);
                if (job?.status) {
                    updateAssignOpenShiftsJobStatus(job.status as JobStatus);
                    if (TERMINAL_STATUSES.includes(job.status as JobStatus)) {
                        this.stopPolling();

                        // Snapshot the active job and pre-refresh slot counts BEFORE branching on
                        // Success / Failure / Canceled. Both Success and Failure paths log the
                        // AssignCompleted event with `NumShiftsAssigned` / `NumShiftsUnassigned`
                        // and `TimeToAssign`, so they share the same snapshot inputs.
                        const activeJobSnapshot = assignOpenShiftsPollingState().activeJob;
                        const startTimestampSnapshot = activeJobSnapshot?.startTimestamp;
                        // Slot snapshot — mirrors the dialog's counting rules:
                        // 1. Draft or published (isPublished is not filtered) — same as dialog group count.
                        // 2. state === Active only — deleted shifts still present in the store are excluded.
                        // 3. Tag matched via tagIds[0] (ShiftUtils.getTagIdFromShift) — same as dialog and schedule view.
                        // Use openSlots (the live count); sharedChanges.openSlots is the last-published
                        // snapshot and does not update when the auto-assign job fills slots.
                        const originalSlotsByShiftId = new Map<string, number>();
                        Array.from(schedulesViewStateStore().openShiftsInView.values()).forEach((os: IOpenShiftEntity) => {
                            if (
                                ShiftUtils.isActiveShift(os) &&
                                ShiftUtils.getTagIdFromShift(os) === tagId &&
                                os.openSlots > 0
                            ) {
                                originalSlotsByShiftId.set(os.id, os.openSlots);
                            }
                        });
                        let totalSlotsAtStart = 0;
                        originalSlotsByShiftId.forEach(slots => { totalSlotsAtStart += slots; });
                        const timeToAssign = startTimestampSnapshot ? (Date.now() - startTimestampSnapshot) : 0;

                        if (job.status === JobStatus.Failure) {
                            setShowFailureToast(tagId, teamId);

                            // Log AssignCompleted for the failure path. Nothing got assigned, so
                            // `NumShiftsUnassigned` is the full pre-job slot count and ZeroShiftsAssigned is true.
                            // `NumberOpenShifts` mirrors AssignClicked semantics — the tag-scoped open-slot
                            // count when the job started (== totalSlotsAtStart).
                            InstrumentationService.logEvent(InstrumentationService.events.AssignOpenShiftsAssignCompleted, [
                                getGenericEventPropertiesObject(InstrumentationService.properties.Success, false),
                                getGenericEventPropertiesObject(InstrumentationService.properties.AllShiftsAssigned, false),
                                getGenericEventPropertiesObject(InstrumentationService.properties.NumShiftsAssigned, 0),
                                getGenericEventPropertiesObject(InstrumentationService.properties.NumShiftsUnassigned, totalSlotsAtStart),
                                getGenericEventPropertiesObject(InstrumentationService.properties.TimeToAssign, timeToAssign),
                                getGenericEventPropertiesObject(InstrumentationService.properties.NumberOpenShifts, totalSlotsAtStart),
                                getGenericEventPropertiesObject(InstrumentationService.properties.ZeroShiftsAssigned, true)
                            ]);
                        } else if (job.status === JobStatus.Success) {
                            const preRefreshViewState = schedulesViewStateStore();
                            const undoData: UndoData = {
                                tagId,
                                teamId,
                                startDate: moment(preRefreshViewState.viewStartDate).utc().format("YYYY-MM-DD"),
                                endDate: moment(preRefreshViewState.viewEndDate).utc().format("YYYY-MM-DD")
                            };

                            // Snapshot reassignParams / skipTrackChanges BEFORE the refresh await — if a Redo
                            // races into this window it would call setAssignOpenShiftsJob({ ..., skipTrackChanges: true })
                            // and flip the live state to a different job, which would otherwise cause us to
                            // silently skip the push for THIS (just-completed) assignment.
                            const reassignParamsSnapshot = activeJobSnapshot?.reassignParams;
                            const skipTrackChangesSnapshot = activeJobSnapshot?.skipTrackChanges;

                            try {
                                await DataServices.TeamDataService.getDataInDateRange(
                                    teamId,
                                    preRefreshViewState.viewStartDate,
                                    preRefreshViewState.viewEndDate,
                                    true /* forceLoadFromNetwork */
                                );
                            } catch (e) {
                                trace.warn(`AssignOpenShiftsPollingService: data refresh error: ${e?.message}`);
                            }

                            // Calculate assigned vs total slots to determine full vs partial success.
                            // Use openSlots post-refresh for the same reason as above.
                            const refreshedView = schedulesViewStateStore();
                            let totalSlots = 0;
                            let assignedSlots = 0;
                            originalSlotsByShiftId.forEach((originalSlots, shiftId) => {
                                totalSlots += originalSlots;
                                const refreshed = refreshedView.openShiftsInView.get(shiftId) as IOpenShiftEntity | undefined;
                                // Do not guard on isPublished here: after auto-assign the entity may transition
                                // to a draft state (isPublished = false) even though slots weren't filled.
                                // If the entity is found by ID, its openSlots reflects the actual remaining count.
                                // If not found (all slots filled, entity removed), remainingSlots = 0 is correct.
                                const remainingSlots = refreshed ? refreshed.openSlots : 0;
                                assignedSlots += (originalSlots - remainingSlots);
                            });

                            const isPartial = totalSlots > 0 && assignedSlots < totalSlots;
                            if (isPartial) {
                                setPartialSuccessToast(assignedSlots, totalSlots, undoData);
                            } else {
                                setShowSuccessToast(true, undoData, assignedSlots);
                            }

                            // Push the assignment onto the undo stack so hamburger Undo / Redo and Ctrl+Z / Ctrl+Y
                            // can revert and replay it. Uses the snapshot captured before the refresh await so a
                            // racing Redo can't flip the read state. Skip when the active job was kicked off by
                            // Redo (skipTrackChanges=true) — in that case revertChanges already returned a synthetic
                            // entity that landed on the undo stack. Gated on EnableAssignOpenShifts: if the feature
                            // is off, nothing lands on the stack, so hamburger / keyboard / toast all see no assign
                            // entry to act on.
                            const isAssignOpenShiftsEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableAssignOpenShifts);
                            if (isAssignOpenShiftsEnabled && !skipTrackChangesSnapshot && reassignParamsSnapshot) {
                                try {
                                    // Lazy-require breaks an import cycle (helper imports sh-services which exports
                                    // this polling service) AND ensures the helper isn't pulled into module graphs
                                    // (e.g. test harness setup) that load sh-services eagerly. The `as typeof import`
                                    // cast restores compile-time type checking on the call below — without it, a
                                    // future signature change to the helper would silently skip typecheck here.
                                    // eslint-disable-next-line @typescript-eslint/no-var-requires
                                    const { buildAssignOpenShiftsChangeEntity } = require("sh-application/components/autoSchedule/assignOpenShiftsChangeTasks") as typeof import("sh-application/components/autoSchedule/assignOpenShiftsChangeTasks");
                                    const entity = buildAssignOpenShiftsChangeEntity(undoData, reassignParamsSnapshot);
                                    trackChanges([entity]);
                                } catch (e) {
                                    trace.warn(`AssignOpenShiftsPollingService: trackChanges failed: ${e?.message}`);
                                }
                            }

                            // Log AssignCompleted for the success path (covers full and partial success).
                            // `NumberOpenShifts` mirrors AssignClicked semantics — the tag-scoped open-slot
                            // count when the job started. ZeroShiftsAssigned == true here flags "ran fine
                            // but couldn't fill anything" (eligible-member shortage / constraints too tight),
                            // distinct from the failure-path zero (algorithm errored).
                            InstrumentationService.logEvent(InstrumentationService.events.AssignOpenShiftsAssignCompleted, [
                                getGenericEventPropertiesObject(InstrumentationService.properties.Success, true),
                                getGenericEventPropertiesObject(InstrumentationService.properties.AllShiftsAssigned, !isPartial),
                                getGenericEventPropertiesObject(InstrumentationService.properties.NumShiftsAssigned, assignedSlots),
                                getGenericEventPropertiesObject(InstrumentationService.properties.NumShiftsUnassigned, totalSlots - assignedSlots),
                                getGenericEventPropertiesObject(InstrumentationService.properties.TimeToAssign, timeToAssign),
                                getGenericEventPropertiesObject(InstrumentationService.properties.NumberOpenShifts, totalSlotsAtStart),
                                getGenericEventPropertiesObject(InstrumentationService.properties.ZeroShiftsAssigned, assignedSlots === 0)
                            ]);
                        }
                        // JobStatus.Canceled intentionally does not log AssignCompleted — cancel is a
                        // user action, not a completion, and is covered by AssignOpenShifts_CancelClicked.
                        clearAssignOpenShiftsJob();
                    }
                }
            } catch (e) {
                trace.warn(`AssignOpenShiftsPollingService polling error: ${e?.message}`);
            }
        };

        this._pollHelper = new PollHelper(POLL_INTERVAL_MS, POLL_INTERVAL_MS, performPoll);
    }

    public stopPolling(): void {
        if (this._pollHelper) {
            this._pollHelper.stopPolling();
            this._pollHelper = undefined;
        }
        this._isPolling = false;
    }
}

const service = new AssignOpenShiftsPollingService();
export default service as AssignOpenShiftsPollingService;
